// Camp data, roster mode: everyone registered for the camp on the left ("N of 15 recorded"), one athlete's
// measurables on the right, Prev / Save & next. Saves exactly what the one-stat station saves: update-stat,
// then a camp-tagged verify, so the values land in the same place with the same audit trail.
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { STAT_OPTIONS, SECTION_TITLES, ALL_STATS } from '../../lib/campStats';
import '../lists/Lists.css';

const TOTAL = ALL_STATS.length;
const recordedCount = (a) => ALL_STATS.filter((s) => a.recorded && a.recorded[s.value] != null && a.recorded[s.value] !== '').length;
const titleOf = (a) => (a.jersey_number != null ? `#${a.jersey_number} ${a.name}` : a.name);

export default function CampDataRoster({ campId, onCaptureHand }) {
  const { API_URL } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [roster, setRoster] = useState(null);     // null = loading
  const [failed, setFailed] = useState(false);
  const [find, setFind] = useState('');
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setRoster(null); setFailed(false);
    if (!campId) return undefined;
    let cancelled = false;
    axios.get(`${API_URL}/camps/${campId}/data-roster`)
      .then((r) => { if (!cancelled) setRoster(r.data.athletes || []); })
      .catch(() => { if (!cancelled) { setFailed(true); setRoster([]); } });
    return () => { cancelled = true; };
  }, [API_URL, campId]);

  const athletes = useMemo(() => roster || [], [roster]);
  const q = find.trim().toLowerCase();
  const shown = athletes.filter((a) => !q || a.name.toLowerCase().includes(q));
  const wanted = Number(searchParams.get('athlete')) || null;
  const selected = athletes.find((a) => a.user_id === wanted) || athletes[0] || null;
  const selId = selected?.user_id;
  const position = shown.findIndex((a) => a.user_id === selId);

  // A new athlete starts from what is recorded for them, with nothing typed.
  useEffect(() => { setDrafts({}); setError(''); }, [selId]);

  const select = (id) => setSearchParams({ athlete: String(id) }, { replace: true });
  const go = (delta) => {
    const next = shown[position + delta];
    if (next) select(next.user_id);
  };

  const valueOf = (stat) => (drafts[stat] !== undefined ? drafts[stat] : (selected?.recorded?.[stat] ?? ''));
  const changed = selected
    ? ALL_STATS.filter((s) => drafts[s.value] !== undefined && String(drafts[s.value]).trim() !== ''
        && String(drafts[s.value]).trim() !== String(selected.recorded?.[s.value] ?? ''))
    : [];

  const saveAndNext = async () => {
    if (!selected || saving) return;
    setSaving(true); setError('');
    const saved = {};
    try {
      for (const stat of changed) {
        const value = String(drafts[stat.value]).trim();
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API_URL}/athletes/${selected.user_id}/update-stat`, { stat_name: stat.value, value });
        // eslint-disable-next-line no-await-in-loop
        await axios.post(`${API_URL}/athletes/${selected.user_id}/verify`,
          { stat: stat.value, verified: true, camp_id: Number(campId) });
        saved[stat.value] = value;
      }
    } catch (e) {
      // Keep what was typed and say which field failed; whatever saved before it stays saved.
      const failedStat = changed.find((s) => saved[s.value] === undefined);
      setError(`${failedStat ? failedStat.label : 'Save'}: ${e.response?.data?.detail?.message || e.response?.data?.detail || 'could not be saved.'}`);
      if (Object.keys(saved).length) {
        setRoster((rs) => rs.map((a) => (a.user_id === selected.user_id ? { ...a, recorded: { ...a.recorded, ...saved } } : a)));
      }
      setSaving(false);
      return;
    }
    if (Object.keys(saved).length) {
      setRoster((rs) => rs.map((a) => (a.user_id === selected.user_id ? { ...a, recorded: { ...a.recorded, ...saved } } : a)));
    }
    setDrafts({});
    setSaving(false);
    go(1);
  };

  if (!campId) return <div className="adm__card"><p className="adm-empty">Choose a camp to record measurables.</p></div>;
  if (roster === null) return <div className="adm__card"><p className="adm-loading">Loading athletes…</p></div>;
  if (failed) return <div className="adm__card"><p className="adm-error" role="alert">Could not load this camp’s athletes.</p></div>;
  if (athletes.length === 0) return <div className="adm__card"><p className="adm-empty">No one is registered for this camp yet.</p></div>;

  return (
    <div className="ls-grid cd-grid">
      <aside className="adm__card ls-index" aria-label="Athletes">
        <input className="ls-find" type="search" placeholder="Find an athlete…" aria-label="Find an athlete" value={find} onChange={(e) => setFind(e.target.value)} />
        {shown.length === 0 && <p className="ls-empty">No athlete matches.</p>}
        {shown.map((a) => (
          <button key={a.user_id} type="button" className={`ls-irow ${a.user_id === selId ? 'ls-irow--active' : ''}`}
            aria-current={a.user_id === selId ? 'true' : undefined} onClick={() => select(a.user_id)}>
            <span className="ls-irow__text">
              <span className="ls-irow__title">{titleOf(a)}</span>
              <span className="ls-irow__sub cd-row__sub">{`${recordedCount(a)} of ${TOTAL} recorded`}</span>
            </span>
          </button>
        ))}
      </aside>

      <section className="adm__card ls-open">
        {selected && (
          <>
            <div className="ls-head">
              <div className="ls-head__main">
                <div className="ls-head__meta">
                  <span className={`ls-chip ${selected.attended ? '' : 'wb-chip--draft'}`}>{selected.attended ? 'Checked in' : 'Not checked in'}</span>
                  <span className="ls-head__hint">{[selected.position, selected.graduation_year, selected.high_school].filter(Boolean).join(' · ')}</span>
                </div>
                <h2 className="wb-title">{titleOf(selected)}</h2>
              </div>
              <div className="ls-head__actions">
                <button type="button" className="ls-btn" disabled={position <= 0} onClick={() => go(-1)}>‹ Prev</button>
                <button type="button" className="ls-btn" disabled={position < 0 || position >= shown.length - 1} onClick={() => go(1)}>Next ›</button>
              </div>
            </div>
            {error && <div className="ls-banner ls-banner--error" role="alert">{error}</div>}
            {STAT_OPTIONS.map((g) => (
              <div className="cd-section" key={g.group}>
                <div className="srch-label">{SECTION_TITLES[g.group] || g.group}</div>
                <div className="cd-tiles">
                  {g.stats.map((s) => (
                    <label className="cd-tile" key={s.value}>
                      <span className="cd-tile__label">{s.label}</span>
                      <input className="cd-tile__input" type="number" step="any" aria-label={s.label} value={valueOf(s.value)}
                        onChange={(e) => setDrafts((d) => ({ ...d, [s.value]: e.target.value }))} />
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <div className="cd-foot">
              <button type="button" className="ls-btn" onClick={() => onCaptureHand(selected)}>Capture hand photo</button>
              <button type="button" className="ls-btn ls-btn--primary" disabled={saving} onClick={saveAndNext}>
                {saving ? 'Saving…' : 'Save & next'}
              </button>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
